import AlbumIcon from "@mui/icons-material/Album";
import HomeIcon from "@mui/icons-material/Home";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, Typography } from "@mui/material";
import React, { useEffect, useId, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { cap, getArtistPath, lc, trackClick } from "../_helpers";
import Layout from "../layout";
import { ArtistNav } from "../templates/ArtistTemplate/ArtistNav";
import { API_CACHE_TTL, fetchCachedGetApi } from "../../utils/apiCache";
import { toArtistSlug } from "../../utils/artistSlug";
import animationData from "./404a.json";
import "./trackNotFound.scss";

interface ArtistData {
  name: string;
  images?: Array<{ url: string }>;
  external_urls?: { spotify: string };
}

interface Track404Props {
  song: {
    artist: string;
    artistUsername?: string;
    title?: string;
    artistLogo?: boolean;
  };
  pageTitle?: string;
  message?: string;
  errorType?: "track" | "album" | "page" | "blog";
  showGrain?: boolean;
  skipArtistFetch?: boolean;
  temporary?: boolean;
}

const TRUEVINED_SPOTIFY = "https://open.spotify.com/artist/256m8qrr1OX3cxhHpnsNJe";

const pillSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "48px",
  px: "22px",
  borderRadius: "999px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: 1,
  letterSpacing: "0.01em",
  textDecoration: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
  transition: "background-color 160ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
  "& svg": { fontSize: "18px" },
  "&:active": { transform: "scale(0.97)" },
  "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "3px" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
} as const;

const primarySx = {
  ...pillSx,
  border: 0,
  backgroundColor: "var(--logoColor2)",
  color: "var(--color1)",
  "&:hover": { backgroundColor: "color-mix(in srgb, var(--logoColor2) 86%, #ffffff)", color: "var(--color1)" },
} as const;

const secondarySx = {
  ...pillSx,
  border: "1px solid color-mix(in srgb, var(--logoColor) 34%, transparent)",
  backgroundColor: "transparent",
  color: "var(--logoColor)",
  "&:hover": { backgroundColor: "color-mix(in srgb, var(--logoColor) 9%, transparent)", color: "var(--logoColor)" },
} as const;

function TintedIllustration() {
  const filterId = `track404-tint-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [Player, setPlayer] = useState<React.ComponentType<Record<string, unknown>> | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (typeof window.matchMedia === "function") setReducedMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    import("react-lottie-player/dist/LottiePlayerLight")
      .then((mod) => {
        if (!cancelled) setPlayer(() => mod.default as unknown as React.ComponentType<Record<string, unknown>>);
      })
      .catch((error) => {
        console.error("Failed to load 404 animation:", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      aria-hidden
      data-testid="track404-illustration"
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: { xs: "320px", md: "440px" },
        aspectRatio: { xs: "4 / 3", md: "1 / 1" },
        mx: { xs: "auto", md: 0 },
        justifySelf: { md: "end" },
        "& > div svg": { filter: `url(#${filterId})` },
      }}
    >
      <svg width="0" height="0" focusable="false" style={{ position: "absolute" }}>
        <filter id={filterId} x="0" y="0" width="100%" height="100%" colorInterpolationFilters="sRGB">
          <feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  -0.6378 -2.1456 -0.2166 0 2.7" result="coverage" />
          <feComposite in="coverage" in2="SourceAlpha" operator="in" result="shape" />
          <feFlood style={{ floodColor: "var(--logoColor2)" }} result="ink" />
          <feComposite in="ink" in2="shape" operator="in" />
        </filter>
      </svg>
      {Player ? (
        <Player
          loop
          play={!reducedMotion}
          goTo={reducedMotion ? 80 : undefined}
          animationData={animationData}
          rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
          style={{ width: "100%", height: "100%" }}
        />
      ) : null}
    </Box>
  );
}

const Track404: React.FC<Track404Props> = ({
  song,
  pageTitle,
  message,
  errorType = "page",
  showGrain = false,
  skipArtistFetch = false,
  temporary = false,
}) => {
  const [artistData, setArtistData] = useState<ArtistData | null>(null);

  useEffect(() => {
    if (skipArtistFetch || !song?.artist) return;
    const artistUsername = song.artistUsername || toArtistSlug(song.artist);
    fetchCachedGetApi<{ artistData?: ArtistData }>(`/.netlify/functions/SpotifyGetArtist`, { artist: artistUsername }, {
      ttlMs: API_CACHE_TTL.SIX_HOURS,
      shouldCache: (data) => Boolean(data?.artistData),
    })
      .then((data) => {
        if (data.artistData) setArtistData(data.artistData);
      })
      .catch((error) => {
        console.error("Error loading artist:", error);
      });
  }, [skipArtistFetch, song?.artist, song?.artistUsername]);

  if (!song) {
    return null;
  }

  const isBlogError = errorType === "blog";
  const artistSlug = song.artistUsername || song.artist;
  const artistPath = isBlogError ? "/" : getArtistPath(artistSlug);
  const artistName = cap(isBlogError ? "Truevined" : artistData?.name || song.artist || "Truevined");
  const artistImage = artistData?.images?.[0]?.url;
  const showSongs = errorType !== "track" && !isBlogError;
  const showAlbums = errorType !== "album" && !isBlogError;
  const headline = pageTitle || (temporary ? "Be right back" : "Page Not Found");
  const eyebrow = temporary ? "Temporary outage" : isBlogError ? "Old blog link" : "Error 404";
  const click = (target: string, label: string, href?: string) => trackClick(`not-found:${target}`, { label, href, section: "not-found" });

  return (
    <Layout artwork={artistImage} showGrain={showGrain}>
      <ArtistNav artist={artistName} artistImage={artistImage} />
      <Box
        component="main"
        sx={{
          minHeight: "100svh",
          backgroundColor: "var(--backgroundColor)",
          pt: { xs: "9.5rem", md: "8rem" },
          pb: { xs: "4rem", md: "5rem" },
          px: { xs: "2.5rem", md: "5rem" },
        }}
      >
        <Box
          sx={{
            maxWidth: "1120px",
            mx: "auto",
            minHeight: { md: "calc(100svh - 13rem)" },
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.3fr) minmax(0, 0.7fr)" },
            alignItems: "center",
            gap: { xs: "24px", md: "56px" },
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              animation: "track404-in 420ms cubic-bezier(0.23, 1, 0.32, 1) both",
              "@keyframes track404-in": { from: { opacity: 0, transform: "translateY(8px)" }, to: { opacity: 1, transform: "none" } },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Typography
              component="p"
              sx={{
                m: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "13px", md: "12px" },
                fontWeight: 600,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--logoColor)",
                opacity: 0.78,
                "&::before": { content: '""', width: "22px", height: "1px", backgroundColor: "currentColor" },
              }}
            >
              {eyebrow}
            </Typography>

            <Typography
              component="h1"
              sx={{
                m: "14px 0 0",
                fontFamily: "Hanson, Poppins, sans-serif",
                fontWeight: 700,
                fontSize: { xs: "clamp(30px, 8.4vw, 42px)", md: "clamp(40px, 3.6vw, 62px)" },
                lineHeight: 1.04,
                letterSpacing: "-0.01em",
                color: "var(--logoColor2)",
                textWrap: "balance",
              }}
            >
              {headline}
            </Typography>

            <Typography
              component="p"
              sx={{
                m: "18px 0 0",
                maxWidth: "46ch",
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "17px", md: "18px" },
                lineHeight: 1.6,
                color: "var(--logoColor)",
                textWrap: "pretty",
              }}
            >
              <Box component="strong" sx={{ color: "var(--logoColor2)", fontWeight: 600 }}>
                {artistName}
              </Box>{" "}
              {message || "doesn't have this page. Let's get you back to the music."}
            </Typography>

            <Box
              sx={{
                mt: { xs: "26px", md: "34px" },
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                "& > *": { flex: { xs: "1 1 100%", sm: "0 0 auto" } },
              }}
            >
              {temporary ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => {
                    click("retry", headline);
                    window.location.reload();
                  }}
                  sx={primarySx}
                >
                  <RefreshIcon aria-hidden />
                  Try again
                </Box>
              ) : null}

              <Box component={RouterLink} to={artistPath} onClick={() => click("artist-page", `${artistName}'s page`, artistPath)} sx={temporary ? secondarySx : primarySx}>
                <HomeIcon aria-hidden />
                Go to {artistName}'s page
              </Box>

              {isBlogError ? (
                <Box
                  component="a"
                  href={TRUEVINED_SPOTIFY}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => click("spotify", "Listen on Spotify", TRUEVINED_SPOTIFY)}
                  sx={{ ...pillSx, border: 0, backgroundColor: "#1DB954", color: "#0b0b0c", "&:hover": { backgroundColor: "#1ed760", color: "#0b0b0c" } }}
                >
                  Listen on Spotify
                </Box>
              ) : null}
            </Box>

            {showSongs || showAlbums ? (
              <Box
                sx={{
                  mt: { xs: "12px", sm: "14px" },
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: { xs: "12px", sm: "6px 22px" },
                }}
              >
                {[
                  { key: "songs", show: showSongs, label: "All songs", path: getArtistPath(artistSlug, "/songs"), Icon: MusicNoteIcon },
                  { key: "albums", show: showAlbums, label: "All albums", path: getArtistPath(artistSlug, "/albums"), Icon: AlbumIcon },
                ]
                  .filter((link) => link.show)
                  .map(({ key, label, path, Icon }) => (
                    <Box
                      key={key}
                      component={RouterLink}
                      to={path}
                      onClick={() => click(key, label, path)}
                      sx={{
                        ...secondarySx,
                        flex: { xs: "1 1 calc(50% - 6px)", sm: "0 0 auto" },
                        minHeight: { xs: "48px", sm: "40px" },
                        px: { xs: "22px", sm: "2px" },
                        border: { xs: secondarySx.border, sm: "1px solid transparent" },
                        borderRadius: { xs: "999px", sm: "6px" },
                        fontWeight: { xs: 600, sm: 500 },
                        "&:hover": {
                          backgroundColor: { xs: "color-mix(in srgb, var(--logoColor) 9%, transparent)", sm: "transparent" },
                          color: "var(--logoColor)",
                          textDecoration: { xs: "none", sm: "underline" },
                          textUnderlineOffset: "4px",
                        },
                      }}
                    >
                      <Icon aria-hidden />
                      {label}
                    </Box>
                  ))}
              </Box>
            ) : null}
          </Box>

          <Box sx={{ order: { xs: -1, md: 0 }, minWidth: 0, display: "grid" }}>
            <TintedIllustration />
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Track404;
